import { cloneDeep, isEmpty } from 'lodash-es';
import { JSX } from 'react';

import {
  defaultDaypartingParsed,
  defaultDaypartingUI,
  emptyDaypartingDays,
  generateDaypartingPeriods,
  IDaypartingDays,
  IDaypartingParsed,
  IDaypartingUI,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/common';
import {
  defaultLocalTargetingUI,
  ILocalTargetingParsed,
  ILocalTargetingUI,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder/common';
import { TKey } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import {
  DeviceOptions,
  DeviceORTBTypeOptions,
  PodSequenceTargetingOptions,
  PositionTargetingOptions,
  SamsungDeviceModelOptions,
  SamsungDeviceSubTypeOptions,
  SlotOptions,
  TargetingLiveStreamOptions,
} from '~/src/dash/utils/options';

export interface IParameterOption {
  key: string;
  label: string;
  isCategory?: boolean;
  category: string;
  tKey?: TKey;
  selectCategoryTKey?: TKey;
}

export interface IParameterInfo {
  key: string;
  label: string; // displayed title
  reactKey?: number; // DO NOT POPULATE. this is automated // number that will be converted to string to be used as a unique key
  Component?: (props: any) => JSX.Element;
  props?: Record<string, any>;
  apiKeys: string[];
}

const DEFAULT_STRING = 'Default';

export const emptyParameterInfo: IParameterInfo = {
  key: DEFAULT_STRING,
  label: '',
  apiKeys: [],
};

export const extractActiveParamKeys = (parameters: IParameterInfo[]): Set<string> => {
  const keys: string[] = [];
  parameters.forEach((param) => {
    if (param.key && param.key !== DEFAULT_STRING) {
      keys.push(param.key);
    }
  });
  return new Set(keys);
};

export const checkForDuplicates = (array: string[]) => {
  return new Set(array).size !== array.length;
};

export const convertDevicesSelectedValue = (value: Set<number>) => {
  if (value.size === DeviceOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

export const convertDevicesResponseValue = (value: string) => {
  switch (true) {
    case !value:
    case value === '0':
      return DeviceOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

export const convertSlotsSelectedValue = (value: Set<number>) => {
  if (value.size === SlotOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

export const convertSlotsResponseValue = (value: string) => {
  switch (true) {
    case !value:
    case value === '0':
      return SlotOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

export const convertPositionTargetingSelectedValue = (value: Set<number>) => {
  if (value.size === PositionTargetingOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

// convertPositionTargetingSelectedValueLimitOne
// return the first selected value of the PositionTargeting
export const convertPositionTargetingSelectedValueLimitOne = (value: Set<number>): number => {
  if (value.size === 0) return 0;
  return [...value][0];
};

export const convertStringOrNumberResponseValue = (value: string) => {
  return [value ? Number(value) : 0];
};

export const convertPodSequenceTargetingSelectedValue = (value: Set<number>) => {
  if (value.size === PodSequenceTargetingOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

// convertPodSequenceTargetingSelectedValueLimitOne
// return the first selected value of the PodSequenceTargeting
export const convertPodSequenceTargetingSelectedValueLimitOne = (value: Set<number>): number => {
  if (value.size === 0) return 0;
  return [...value][0];
};

export const convertDeviceTypesSelectedValue = (value: Set<number>) => {
  if (value.size === DeviceORTBTypeOptions.length) {
    return '';
  } else {
    return Array.from(value).join(',');
  }
};

export const convertDeviceTypesResponseValue = (value: string) => {
  switch (true) {
    case !value:
      return DeviceORTBTypeOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

export const convertDeviceSubTypesSelectedValue = (value: Set<number>) => {
  const size = value.size;
  if (size === 0 || size === SamsungDeviceSubTypeOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

export const convertDeviceSubTypesResponseValue = (value: string) => {
  switch (true) {
    case !value:
    case value === '0':
      return SamsungDeviceSubTypeOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

export const convertDeviceModelsSelectedValue = (value: Set<number>): string => {
  const size = value.size;
  if (size === 0 || size === SamsungDeviceModelOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

export const convertDeviceModelsResponseValue = (value: string): (string | number)[] => {
  switch (true) {
    case !value:
    case value === '0':
      return SamsungDeviceModelOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

export const convertLivestreamResponseValue = (value: string) => {
  if (!value || value === '0') {
    return TargetingLiveStreamOptions.map((option) => option.id);
  } else {
    return value.split(',').map(Number);
  }
};

export const convertLivestreamSelectedValue = (value: Set<number>) => {
  if (value.size === TargetingLiveStreamOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

export const convertStringToTrimmedArray = (value: string) => {
  if (!value) return [];
  return value.split(',').map((s) => s.trim());
};

export const convertLocalTargetingUI = (locations: ILocalTargetingUI[]) => {
  const result: ILocalTargetingParsed[] = [];
  const errors: Set<string> = new Set([]);
  locations.forEach((loc) => {
    if (loc.country) {
      // validate
      // states
      const states = convertStringToTrimmedArray(loc.states);
      if (checkForDuplicates(states)) {
        errors.add('Please remove duplicate state values.');
      }
      // cities
      const cities = convertStringToTrimmedArray(loc.cities);
      if (checkForDuplicates(cities)) {
        errors.add('Please remove duplicate city values.');
      }
      // zips
      const zips = convertStringToTrimmedArray(loc.zips);
      if (zips.some((zip) => zip.length < 3 && zip !== Number(zip).toString())) {
        errors.add('Invalid zip codes found in Local Targeting.');
      }
      if (checkForDuplicates(zips)) {
        errors.add('Please remove duplicate zip codes.');
      }
      // dmas
      const dmas = convertStringToTrimmedArray(loc.dmas);
      if (checkForDuplicates(dmas)) {
        errors.add('Please remove duplicate DMAs.');
      }

      result.push({
        type: loc.type,
        country: loc.country,
        states: states.join(','),
        areStatesCommaSeparated: !!loc.areStatesCommaSeparated,
        cities: cities.join(','),
        areCitiesCommaSeparated: !!loc.areCitiesCommaSeparated,
        zips: zips.join(','),
        dmas: dmas.join(','),
        areDMAsCommaSeparated: !!loc.areDMAsCommaSeparated,
      });
    }
  });

  return {
    value: result.length < 1 ? '' : JSON.stringify(result),
    errors: Array.from(errors),
  };
};

export const convertDaypartingUI = (props: IDaypartingUI) => {
  const errors: string[] = [];
  const { Periods, EnableViewerTimezone, Enabled, Timezone } = props;
  const daypartingDays = cloneDeep(emptyDaypartingDays);
  const dayKeys = Object.keys(daypartingDays);

  if (Periods.length > 0) {
    Object.values(Periods).forEach((period) => {
      period.days.forEach((day: keyof IDaypartingDays) => {
        daypartingDays[day].push([period.start, period.end]);
      });
    });
  }

  // sort intervals
  dayKeys.forEach((dayKey: keyof IDaypartingDays) => {
    daypartingDays[dayKey] = daypartingDays[dayKey].sort((a, b) => {
      if (a[0].isBefore(b[0])) {
        return -1;
      } else if (a[0].isAfter(b[0])) {
        return 1;
      }
      return 0;
    });
  });

  // merge intervals
  dayKeys.forEach((dayKey: keyof IDaypartingDays) => {
    const results = [] as [moment.Moment, moment.Moment][];
    daypartingDays[dayKey].forEach((interval) => {
      const prev = results[results.length - 1];
      if (!results.length || prev[1].isBefore(interval[0])) {
        results.push(interval);
      } else {
        if (prev[1].isBefore(interval[1])) {
          prev[1] = interval[1];
        }
      }
      daypartingDays[dayKey] = results;
    });
  });

  const emptyDayparting = cloneDeep(defaultDaypartingParsed);

  // convert moments to string
  dayKeys.forEach((dayKey: keyof IDaypartingDays) => {
    daypartingDays[dayKey].forEach((interval) => {
      emptyDayparting.Schedule[dayKey].push([
        interval[0].format('HH:mm'),
        interval[1].format('HH:mm'),
      ]);
    });
  });

  // populate rest for conversion
  emptyDayparting.EnableViewerTimezone = EnableViewerTimezone;
  emptyDayparting.Enabled = Enabled;
  emptyDayparting.Timezone = Timezone;

  return {
    value: JSON.stringify(emptyDayparting),
    errors,
  };
};

export const generateGenericPlaceholder = (label: string) => {
  return `Enter ${label} here, comma separated.`;
};

export const generateContentPlaceholder = (label: string) => {
  return `Enter ${label} to be bid on here, comma separated.`;
};

export const generateCustomPlaceholder = (label: string) => {
  return `Enter ${label} here. Can be comma separated.`;
};

export const parseLocalTargetingForUI = (localTargetingBlob: string) => {
  const localTargeting: ILocalTargetingParsed[] = !!localTargetingBlob
    ? JSON.parse(localTargetingBlob)
    : [];

  const localTargetingUI: ILocalTargetingUI[] = [];
  let reactKey = 1001;
  localTargeting.forEach((lt) => {
    localTargetingUI.push({
      type: lt.type || 'country',
      country: lt.country || '',
      states: lt.states || '',
      areStatesCommaSeparated: !!lt.areStatesCommaSeparated,
      cities: lt.cities || '',
      areCitiesCommaSeparated: !!lt.areCitiesCommaSeparated,
      zips: lt.zips || '',
      dmas: lt.dmas || '',
      areDMAsCommaSeparated: !!lt.areDMAsCommaSeparated,
      reactKey,
    });
    reactKey++;
  });

  if (isEmpty(localTargetingUI)) {
    localTargetingUI.push(cloneDeep(defaultLocalTargetingUI));
    localTargetingUI[0].reactKey = reactKey;
  }

  return localTargetingUI;
};

export const parseDaypartingForUI = (
  daypartingBlob: string,
  session: ISessionState,
): IDaypartingUI => {
  const dayparting: IDaypartingParsed = !!daypartingBlob ? JSON.parse(daypartingBlob) : {};

  const daypartingUI = cloneDeep(defaultDaypartingUI);

  daypartingUI.Timezone = dayparting?.Timezone || session.publisher.Timezone;
  daypartingUI.Enabled = dayparting?.Enabled || false;
  daypartingUI.EnableViewerTimezone = dayparting?.EnableViewerTimezone || false;

  const schedule = dayparting?.Schedule || defaultDaypartingParsed.Schedule;
  daypartingUI.Periods = generateDaypartingPeriods(schedule);

  return daypartingUI;
};

export const validateTextInput = (value: string, parameterLabel: string) => {
  if (!value) {
    return [`${parameterLabel} is empty.`];
  }
  return [];
};

export const parseDaypartingForUIV2 = (
  daypartingBlob: string,
  defaultTimezone: string,
): IDaypartingUI => {
  // eslint-disable-next-line  @typescript-eslint/no-unsafe-assignment
  const dayparting: IDaypartingParsed = !!daypartingBlob ? JSON.parse(daypartingBlob) : {};

  const daypartingUI = cloneDeep(defaultDaypartingUI);

  daypartingUI.Timezone = dayparting?.Timezone || defaultTimezone;
  daypartingUI.Enabled = dayparting?.Enabled || false;
  daypartingUI.EnableViewerTimezone = dayparting?.EnableViewerTimezone || false;

  const schedule = dayparting?.Schedule || defaultDaypartingParsed.Schedule;
  daypartingUI.Periods = generateDaypartingPeriods(schedule);

  return daypartingUI;
};
